import useSWR from "swr";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

import {
  QuickSubscribeCardProps,
  SubscriptionPreviewType,
} from "@bluebottlecoffee/design-system";
import { SelectionCardProps } from "@bluebottlecoffee/design-system/dist/QuickSubscribeCard/components/SelectionCard";
import { ShopCardPropsWithSlug } from "../../transformers/shop-card";
import { AlgoliaCollectionPreview } from "../../algolia/collection-preview";
import { RootState } from "../../chord/types";
import {
  ADD_SITE_WIDE_PROMOTION,
  REMOVE_SITE_WIDE_PROMOTION,
} from "../../chord/packages/web/src/redux/actions/types";
import { fetcher } from "../fetcher";
import { ConversionSchema } from "../../transformers/conversion";
import {
  elementTypeisConversion,
  elementTypeisCollection,
  elementTypeisShopCards,
  elementTypeisSubscriptions,
  cast,
} from "../../utils";
import { DereferencedVariant } from "../../sanity/variant-queries";

interface UseSiteWidePromoOptions {
  /**
   * This property force the return value when
   * a product has subscription options.
   */
  forceReturnValue?: boolean;
}

export const useSiteWidePromo = <T>(
  value: T,
  options?: UseSiteWidePromoOptions,
): T => {
  const { forceReturnValue } = options || {};
  const dispatch = useDispatch();
  const { siteWidePromotion } = useSelector((state: RootState) => state);
  const [discountedItem, setDiscountedItem] = useState(value);

  // env var only needs to be populated in development/local environments
  const base = process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_HOST || "";
  const endpoint = `${base}/api/v1/promotions/site-wide-promotion`;

  const { data } = useSWR([endpoint, value], (url) => fetcher(url));

  const calculateDiscount = (
    regularPrice: number,
    price: number,
    discount: number,
  ) => {
    const discountedPrice = regularPrice * (1 - discount / 100);

    return price < discountedPrice ? price : discountedPrice;
  };

  const formatVariant = (variant: DereferencedVariant) => ({
    ...variant,
    price: calculateDiscount(
      variant.regularPrice,
      variant.price,
      data.discount,
    ),
  });

  const formatCard = (
    card: ShopCardPropsWithSlug | QuickSubscribeCardProps,
  ): ShopCardPropsWithSlug | QuickSubscribeCardProps => ({
    ...card,
    bestValueVariant: {
      ...card.bestValueVariant,
      price: {
        ...card.bestValueVariant.price,
        amount: calculateDiscount(
          card.bestValueVariant.regularPrice.amount,
          card.bestValueVariant.price.amount,
          data.discount,
        ),
      },
    },
  });

  const formatSelection = (
    selection: SelectionCardProps,
  ): SelectionCardProps => ({
    ...selection,
    product: {
      ...selection.product,
      variants: selection.product.variants.map((variant) => ({
        ...variant,
        price: {
          ...variant.price,
          amount: calculateDiscount(
            variant.regularPrice.amount,
            variant.price.amount,
            data.discount,
          ),
        },
      })),
    },
  });

  const collectionDiscount = () =>
    cast<AlgoliaCollectionPreview[], T>(value).map((collection) => ({
      ...collection,
      shopCards: collection.shopCards.map((card) => ({
        ...formatCard(card),
      })),
    }));

  const shopCardsDiscount = () =>
    cast<ShopCardPropsWithSlug[], T>(value).map((card) => ({
      ...formatCard(card),
    }));

  const subscriptionsDiscount = () =>
    cast<SubscriptionPreviewType[], T>(value).map((subscription) => ({
      ...subscription,
      cards: subscription.cards.map((card) => ({
        ...formatCard(card),
        selection: {
          ...formatSelection(card.selection),
        },
      })),
    }));

  const productDiscount = () => {
    const variants = cast<ConversionSchema, T>(value).variants.map(
      (variant) => ({
        ...formatVariant(variant),
      }),
    );
    return { ...value, variants };
  };

  const applyDiscount = () => {
    if (elementTypeisConversion(value)) return productDiscount();

    if (elementTypeisCollection(value)) return collectionDiscount();

    if (elementTypeisShopCards(value)) return shopCardsDiscount();

    if (elementTypeisSubscriptions(value) && data?.includeSubscriptions)
      return subscriptionsDiscount();

    return value;
  };

  useEffect(() => {
    if (data?.discount) {
      dispatch({
        type: ADD_SITE_WIDE_PROMOTION,
        payload: { ...data },
      });
      setDiscountedItem(cast<T, any>(applyDiscount()));
    }
    if (siteWidePromotion.discount && !data?.discount) {
      dispatch({
        type: REMOVE_SITE_WIDE_PROMOTION,
      });
    }
  }, [data?.discount]);

  useEffect(() => {
    if (!data?.includeSubscriptions && forceReturnValue) {
      setDiscountedItem(value);
    } else if (data?.discount) {
      setDiscountedItem(cast<T, any>(applyDiscount()));
    }
  }, [forceReturnValue]);

  useEffect(() => {
    setDiscountedItem(value);
  }, [JSON.stringify(value)]);

  return discountedItem;
};
